import '../styles/SignUpEnrollmentPanel.css';
import React, { useRef, useState } from 'react';

const ROLES = [
  { value: 'operator', name: 'Operator', desc: 'Runs authorized credential tests and reads logs.' },
  { value: 'administrator', name: 'Administrator', desc: 'Owns settings, the local store and integrations.' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validate(values) {
  const errors = {};
  const identity = values.identity.trim();
  if (!identity) errors.identity = 'Enter the identity you will enroll with.';
  else if (!EMAIL_RE.test(identity)) errors.identity = 'Use a full address, like name@brute.local.';
  if (!values.credential) errors.credential = 'Enter a credential for this identity.';
  else if (values.credential.length < 8) errors.credential = 'Use at least 8 characters.';
  if (!values.confirmCredential) errors.confirmCredential = 'Repeat the credential to confirm it.';
  else if (values.credential !== values.confirmCredential) errors.confirmCredential = 'The two credentials do not match.';
  return errors;
}

export default function SignUpEnrollmentPanel({ onSubmit }) {
  const [values, setValues] = useState({ identity: '', credential: '', confirmCredential: '', role: 'operator' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState('idle'); // idle | busy | error | success
  const [submitError, setSubmitError] = useState('');
  const [createdIdentity, setCreatedIdentity] = useState(null);
  const alertRef = useRef(null);

  const setField = (key) => (event) => {
    const value = event.target.value;
    setValues((prev) => ({ ...prev, [key]: value }));
    if (fieldErrors[key]) setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    if (submitStatus === 'error') setSubmitStatus('idle');
  };

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitStatus === 'busy') return;
    const errors = validate(values);
    setFieldErrors(errors);
    if (Object.keys(errors).length) {
      setSubmitStatus('idle');
      return;
    }
    if (typeof onSubmit !== 'function') {
      setSubmitStatus('unavailable');
      return;
    }
    setSubmitStatus('busy');
    setSubmitError('');
    try {
      const result = await onSubmit(values);
      if (result && result.success) {
        setCreatedIdentity(result.identity);
        setValues((prev) => ({ ...prev, credential: '', confirmCredential: '' }));
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
        setSubmitError(result && result.error ? result.error : 'The identity could not be created.');
        window.requestAnimationFrame(() => alertRef.current && alertRef.current.focus());
      }
    } catch (err) {
      setSubmitStatus('error');
      setSubmitError(err && err.message ? err.message : 'The identity could not be created.');
      window.requestAnimationFrame(() => alertRef.current && alertRef.current.focus());
    }
  }

  function resetAfterDuplicate() {
    setSubmitStatus('idle');
    setSubmitError('');
    window.requestAnimationFrame(() => {
      const input = document.getElementById('sep-identity');
      if (input) input.focus();
    });
  }

  const busy = submitStatus === 'busy';
  const roleName = (ROLES.find((r) => r.value === values.role) || ROLES[0]).name;

  return (
    <section className="sep-root" aria-label="Create your operator identity">
      <div data-content-rail="access">
        <div className="sep-split">
          {/* SignUpIdentityPoster */}
          <div className="sep-poster" data-region="identity-poster">
            <div className="sep-sticker" aria-label="Authorized targets only">
              <span className="sep-sticker__dot" aria-hidden="true" />
              Authorized targets only
            </div>
            <div className="sep-poster__body">
              <span className="sep-poster__kicker">New enrollment · 01</span>
              <h1 className="sep-poster__heading">
                <span>Establish</span>
                <span>your operator</span>
                <span className="sep-poster__flip">identity</span>
              </h1>
            </div>
            <div className="sep-poster__footer">
              <p className="sep-poster__statement">
                One local identity, declared with its role. Brute stores settings and run history on this machine.
              </p>
              <span className="sep-poster__trust">LOCAL-FIRST · WINDOWS · NO CLOUD REQUIRED</span>
            </div>
          </div>

          {/* SignUpEnrollmentForm + Outcome + SupportLinks */}
          <div className="sep-panel" data-region="enrollment-panel">
            <div className="sep-panel__head">
              <span className="sep-label">Enrollment</span>
              <h2 className="sep-panel__title">Set up your identity</h2>
            </div>

            {submitStatus === 'unavailable' ? (
              <div className="sep-outcome" role="status" aria-live="polite" data-region="outcome">
                <p className="sep-outcome__body">Enrollment is not available in this preview.</p>
              </div>
            ) : submitStatus === 'success' && createdIdentity ? (
              <div className="sep-outcome sep-outcome--success" role="status" aria-live="polite" data-region="outcome">
                <div className="sep-outcome__head">
                  <span className="sep-outcome__tag sep-outcome__tag--ok">ENROLLED</span>
                  Identity created
                </div>
                <p className="sep-outcome__title">You are enrolled.</p>
                <p className="sep-outcome__body">
                  {createdIdentity.identity} · {roleName}
                </p>
                <div className="sep-outcome__actions">
                  <a className="sep-btn" href="/dashboard">
                    Go to dashboard <span aria-hidden="true">→</span>
                  </a>
                </div>
              </div>
            ) : (
              <form className="sep-form" onSubmit={handleSubmit} noValidate aria-busy={busy}>
                {submitStatus === 'error' && (
                  <div
                    className="sep-outcome sep-outcome--error"
                    role="alert"
                    tabIndex={-1}
                    ref={alertRef}
                    data-region="outcome"
                  >
                    <div className="sep-progress" aria-hidden="true">
                      <span /><span /><span /><span />
                    </div>
                    <div className="sep-outcome__head">
                      <span className="sep-outcome__tag">BLOCKED</span>
                      Identity not created
                    </div>
                    <p className="sep-outcome__body">{submitError}</p>
                    <p className="sep-outcome__body">
                      Correct the identity below and submit again, or sign in if this is already your identity.
                    </p>
                    <div className="sep-outcome__actions">
                      <button type="button" className="sep-btn" onClick={resetAfterDuplicate}>
                        Correct identity
                      </button>
                      <a className="sep-btn sep-btn--ghost" href="/login">
                        Sign in
                      </a>
                    </div>
                  </div>
                )}

                <div className="sep-field">
                  <label className="sep-field__label" htmlFor="sep-identity">
                    Identity
                  </label>
                  <input
                    id="sep-identity"
                    className="sep-field__input"
                    type="email"
                    name="identity"
                    autoComplete="username"
                    placeholder="name@brute.local"
                    value={values.identity}
                    onChange={setField('identity')}
                    aria-invalid={Boolean(fieldErrors.identity)}
                    aria-describedby={fieldErrors.identity ? 'sep-identity-error' : undefined}
                    disabled={busy}
                  />
                  {fieldErrors.identity && (
                    <p className="sep-field__msg" id="sep-identity-error">{fieldErrors.identity}</p>
                  )}
                </div>

                <div className="sep-field">
                  <label className="sep-field__label" htmlFor="sep-credential">
                    Credential
                  </label>
                  <input
                    id="sep-credential"
                    className="sep-field__input"
                    type="password"
                    name="credential"
                    autoComplete="new-password"
                    placeholder="At least 8 characters"
                    value={values.credential}
                    onChange={setField('credential')}
                    aria-invalid={Boolean(fieldErrors.credential)}
                    aria-describedby={fieldErrors.credential ? 'sep-credential-error' : undefined}
                    disabled={busy}
                  />
                  {fieldErrors.credential && (
                    <p className="sep-field__msg" id="sep-credential-error">{fieldErrors.credential}</p>
                  )}
                </div>

                <div className="sep-field">
                  <label className="sep-field__label" htmlFor="sep-confirm">
                    Confirm credential
                  </label>
                  <input
                    id="sep-confirm"
                    className="sep-field__input"
                    type="password"
                    name="confirmCredential"
                    autoComplete="new-password"
                    placeholder="Repeat the credential"
                    value={values.confirmCredential}
                    onChange={setField('confirmCredential')}
                    aria-invalid={Boolean(fieldErrors.confirmCredential)}
                    aria-describedby={fieldErrors.confirmCredential ? 'sep-confirm-error' : undefined}
                    disabled={busy}
                  />
                  {fieldErrors.confirmCredential && (
                    <p className="sep-field__msg" id="sep-confirm-error">{fieldErrors.confirmCredential}</p>
                  )}
                </div>

                <fieldset className="sep-fieldset">
                  <legend className="sep-fieldset__legend">Role</legend>
                  <div className="sep-roles">
                    {ROLES.map((role) => (
                      <label className="sep-role" key={role.value}>
                        <input
                          type="radio"
                          name="role"
                          value={role.value}
                          checked={values.role === role.value}
                          onChange={setField('role')}
                          disabled={busy}
                        />
                        <span className="sep-role__box" aria-hidden="true" />
                        <span className="sep-role__text">
                          <span className="sep-role__name">{role.name}</span>
                          <span className="sep-role__desc">{role.desc}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <button type="submit" className="sep-submit" disabled={busy}>
                  {busy ? (
                    <>
                      Creating
                      <span className="sep-submit__dots" aria-hidden="true">
                        <i /><i /><i />
                      </span>
                    </>
                  ) : (
                    'Create identity'
                  )}
                </button>
                <p className="sep-form__note">
                  Enrollment is recorded on this machine with today&apos;s date.
                </p>
              </form>
            )}

            {/* SignUpSupportLinks */}
            <div className="sep-support" data-region="support">
              <a className="sep-support__link" href="/login">
                Already enrolled? Sign in <span className="sep-support__arrow" aria-hidden="true">→</span>
              </a>
              <a className="sep-support__link" href="/">
                Return to Landing <span className="sep-support__arrow" aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}